import { AdAccountInfo, MetaBuyingOptions } from "@blisspointmedia/bpm-types/dist/MetaBuying";
import React, { useCallback, useMemo, useState } from "react";
import { RouteComponentProps } from "@reach/router";
import "./MetaBuyingTools.scss";
import * as R from "ramda";
import {
  Button,
  ButtonFrameworkVariant,
  ButtonType,
  Dropdown,
  DropdownToggleType,
} from "../Components";
import { Form, ListGroup } from "react-bootstrap";
import { MdDelete, MdDownload, MdOutlineImage } from "react-icons/md";
import { BsPlayBtn } from "react-icons/bs";
import AlertDialog from "../Components/AlertDialog/AlertDialog";
import { useSetError } from "../redux/modals";
import { awaitJSON, MetaLambdaFetch } from "../utils/fetch-utils";

const ONLY_NUMBERS_REGEX = /^\d+$/;

const ASSET_OPTIONS = [
  { label: "Pages", value: "page" },
  { label: "Pixels", value: "pixel" },
  { label: "Image", value: "image" },
  { label: "Video", value: "video" },
];

interface MetaBuyingToolsProps {
  metaBuyingOptions: MetaBuyingOptions;
  selectedAdAccount: AdAccountInfo;
  setSelectedAdAccount: React.Dispatch<React.SetStateAction<AdAccountInfo>>;
  adAccountOptions: AdAccountInfo[];
  setMetaBuyingOptions: React.Dispatch<React.SetStateAction<MetaBuyingOptions | undefined>>;
}

const MetaBuyingTools: React.FC<MetaBuyingToolsProps & RouteComponentProps> = React.memo(
  ({
    metaBuyingOptions,
    selectedAdAccount,
    setSelectedAdAccount,
    adAccountOptions,
    setMetaBuyingOptions,
  }: MetaBuyingToolsProps & RouteComponentProps): JSX.Element => {
    const setError = useSetError();
    const [selectedAsset, setSelectedAsset] = React.useState<string>(ASSET_OPTIONS[0].value);
    const [editing, setEditing] = useState<boolean>(false);

    const assetArr = useMemo(() => {
      if (selectedAsset === "image" || selectedAsset === "video") {
        return [];
      }

      const assetOptions =
        metaBuyingOptions[selectedAdAccount.account_id]?.[`${selectedAsset}s`] || [];
      const formattedAssetOptions = assetOptions.map(asset => {
        return {
          id: asset[`${selectedAsset}_id`],
          name: asset.name,
        };
      });

      return formattedAssetOptions;
    }, [metaBuyingOptions, selectedAdAccount, selectedAsset]);

    const [assetToDelete, setAssetToDelete] = useState<{ id: string; name: string }>();
    const onDelete = useCallback(async () => {
      try {
        await MetaLambdaFetch("/deleteAssetInfo", {
          method: "POST",
          body: {
            assetType: selectedAsset,
            adAccountId: selectedAdAccount.account_id,
            assetId: assetToDelete?.id,
          },
        });

        setMetaBuyingOptions(undefined);
      } catch (e) {
        setError({
          message: e instanceof Error ? e.message : String(e),
          reportError: e,
        });
      }
    }, [assetToDelete, selectedAdAccount, selectedAsset, setError, setMetaBuyingOptions]);

    const deleteAlertDialog = useMemo(() => {
      const assetLabel = selectedAsset === "page" ? "Page" : "Pixel";
      return (
        <AlertDialog
          show={true}
          onHide={() => {
            setAssetToDelete(undefined);
          }}
          icon={<MdDelete size={72} />}
          title={`Delete ${assetLabel}?`}
          body={`This will make the ${assetLabel} "${assetToDelete?.name}" unavailable in the Create tab.`}
          primaryAction={onDelete}
          primaryButtonLabel="Got it!"
          secondaryAction={() => setAssetToDelete(undefined)}
          secondaryButtonLabel="Cancel"
        />
      );
    }, [assetToDelete, selectedAsset, onDelete]);

    const [assetIdToImport, setAssetIdToImport] = useState<string>();
    const [assetToImport, setAssetToImport] = useState<any>();
    const [showImportDialog, setShowImportDialog] = useState<boolean>(false);
    const onSubmit = useCallback(async () => {
      if (
        assetIdToImport &&
        !ONLY_NUMBERS_REGEX.test(assetIdToImport) &&
        selectedAsset !== "image"
      ) {
        setError({
          title: `Invalid ${selectedAsset} id`,
          message: "Assets in Meta use numberical ids.",
        });
        return;
      }

      let assetToSave: any;
      try {
        const res = await MetaLambdaFetch("/fetchAssetInfo", {
          params: {
            assetType: selectedAsset,
            businessManager: selectedAdAccount.business_manager,
            assetId: assetIdToImport,
            adAccountId: selectedAsset === "image" ? selectedAdAccount.account_id : undefined,
          },
        });

        assetToSave = await awaitJSON(res);
      } catch (e) {
        setError({
          message: e instanceof Error ? e.message : String(e),
          reportError: e,
        });
      }

      if (!assetToSave) {
        setError({
          title: `Failed to retrieve ${selectedAsset} from Meta`,
          message: `Please check that the id is correct. If so, please reach out to an engineer in #meta-buying-support. Permissions may need to be set up for this ${selectedAsset}.`,
        });
        return;
      }

      setAssetToImport(assetToSave);
      setShowImportDialog(true);
    }, [assetIdToImport, selectedAdAccount, selectedAsset, setError]);

    const onSave = useCallback(async () => {
      try {
        await MetaLambdaFetch("/saveAssetInfo", {
          method: "POST",
          body: {
            assetType: selectedAsset,
            adAccountId: selectedAdAccount.account_id,
            assetId: assetIdToImport,
            ...assetToImport,
          },
        });
      } catch (e) {
        setError({
          message: e instanceof Error ? e.message : String(e),
          reportError: e,
        });
      }

      setMetaBuyingOptions(undefined);
    }, [
      assetIdToImport,
      assetToImport,
      selectedAdAccount,
      selectedAsset,
      setError,
      setMetaBuyingOptions,
    ]);

    const importAlertDialog = useMemo(() => {
      let assetLabel = "";
      switch (selectedAsset) {
        case "page":
          assetLabel = "Page";
          break;
        case "pixel":
          assetLabel = "Pixel";
          break;
        case "image":
          assetLabel = "Image";
          break;
        case "video":
          assetLabel = "Video";
          break;
      }
      if (assetToImport) {
        return (
          <AlertDialog
            show={true}
            onHide={() => {
              setAssetToDelete(undefined);
            }}
            icon={<MdDelete size={72} />}
            title={`Import ${assetLabel}?`}
            body={`This will make the ${assetLabel} "${
              selectedAsset === "video" ? assetToImport.title : assetToImport?.name
            }" available in the Create tab.`}
            primaryAction={onSave}
            primaryButtonLabel="Got it!"
            secondaryAction={() => {
              setAssetIdToImport(undefined);
              setAssetToImport(undefined);
              setShowImportDialog(false);
            }}
            secondaryButtonLabel="Cancel"
          />
        );
      }
    }, [assetToImport, onSave, selectedAsset]);

    const placeholderText = useMemo(() => {
      let text = "";
      switch (selectedAsset) {
        case "image":
          text = "Copy the hash from Media Library for the image you want to import";
          break;
        case "video":
          text = "Copy the id from Media Library for the video you want to import";
          break;
        case "page":
          text = "Copy the page id from Ads Manager for the page you want to import";
          break;
        case "pixel":
          text = "Copy the pixel id from Ads Manager for the pixel you want to import";
          break;
      }

      return text;
    }, [selectedAsset]);

    return (
      <div className="metaBuyingToolsWidget">
        {showImportDialog && importAlertDialog}
        {assetToDelete && deleteAlertDialog}
        <div className="mbtwHeader">
          <div className="mbtwHeaderLeft">
            <div className="mbtwHeaderTitle">Assets</div>
            <div className="mbtwHeaderDivider"></div>
            <Dropdown
              type={DropdownToggleType.WIDGET_TITLE}
              value={selectedAdAccount.account_name}
              options={adAccountOptions.map(({ account_id, account_name }) => ({
                label: `${account_name} (${account_id})`,
                value: account_id,
              }))}
              onChange={item =>
                setSelectedAdAccount(R.indexBy(R.prop("account_id"), adAccountOptions)[item])
              }
            ></Dropdown>
            <div className="mbtwHeaderDivider"></div>
            <Dropdown
              type={DropdownToggleType.WIDGET_TITLE}
              value={selectedAsset}
              options={ASSET_OPTIONS}
              onChange={selection => setSelectedAsset(selection)}
            ></Dropdown>
          </div>
          <div className="mbtwHeaderRight">
            {selectedAsset !== "image" && selectedAsset !== "video" && (
              <Button
                type={ButtonType.FILLED}
                onClick={() => {
                  if (editing) {
                    setAssetIdToImport(undefined);
                  }
                  setEditing(!editing);
                }}
              >
                {editing ? "Cancel" : "Edit"}
              </Button>
            )}
          </div>
        </div>
        <div className="mbtwBody">
          {selectedAsset === "image" && (
            <div className="iconContainer">
              <MdDownload size={250} color="#6b2def" />
              <MdOutlineImage size={250} color="#6b2def" />
            </div>
          )}
          {selectedAsset === "video" && (
            <div className="iconContainer">
              <MdDownload size={250} color="#6b2def" />
              <BsPlayBtn size={250} color="#6b2def" />
            </div>
          )}
          {["pixel", "page"].includes(selectedAsset) && (
            <Form.Group>
              <ListGroup className="metaBuyingAssetsList">
                {assetArr.map(asset => (
                  <ListGroup.Item key={asset.id} className="metaBuyingAssetsItem">
                    {editing ? (
                      <div className="metaBuyingAssetsRow">
                        <div className="assetName">{asset.name}</div>
                        <Button
                          className="deleteButton"
                          type={ButtonType.EMPTY}
                          variant={ButtonFrameworkVariant.ICON_ONLY}
                          icon={<MdDelete />}
                          size="sm"
                          onClick={() => {
                            setAssetToDelete(asset);
                          }}
                        ></Button>
                      </div>
                    ) : (
                      <div className="assetName">{asset.name}</div>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Form.Group>
          )}
        </div>
        <div className="mbtwFooter">
          <div className="mbtwFooterLeft">
            <div className="formLabel">Import Asset:</div>
            <Form.Control
              className="formControl"
              type="text"
              placeholder={placeholderText}
              value={assetIdToImport || ""}
              onChange={e => setAssetIdToImport(e.target.value)}
            ></Form.Control>
          </div>
          <div className="mbtwFooterRight">
            <Button type={ButtonType.FILLED} onClick={onSubmit} disabled={!assetIdToImport}>
              Submit
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

export default MetaBuyingTools;
